import {
  Component,
  computed,
  contentChild,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { Router, RouterLink } from '@angular/router';
import { UserResource } from '@api';
import { SharedCommonModule } from '@shared/common.module';
import { ImprovedResourceRef } from '@shared/resources/resource';
import { MenuService } from '@shared/services/menu.service';
import { WhitelabelService } from '@shared/services/whitelabel.service';
import { ComponentSize } from '@shared/types';
import { EduIconComponent, Icons } from '@atoms/icon/icon.component';
import { EduUserMenuComponent } from '../../user-menu/user-menu.component';
import { MenuContent, MenuItemContent, MenuItemType, MenuSection } from '../types';
import { sidebarLinkStyles, sidebarSectionStyles, sidebarStyles } from './sidebar.variants';

@Component({
  selector: 'edu-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [
    SharedCommonModule,
    RouterLink,
    EduIconComponent,
    EduUserMenuComponent,
    CdkAccordionModule,
  ],
})
export class EduSidebarComponent {
  private menuService = inject(MenuService);

  isCollapsed = input<boolean>(false);
  variant = input<'default' | 'compact'>('default');
  size = input<ComponentSize>('md');
  menuContents = input<MenuContent[]>([]);

  ctaTemplate = contentChild<TemplateRef<unknown>>('cta');

  renderedMenuContents = computed(() => {
    return this.menuService.filterMenuContents(this.menuContents());
  });

  userResource = input.required<ImprovedResourceRef<UserResource>>();
  logout = output();

  Icon = Icons;
  MenuItemType = MenuItemType;

  router = inject(Router);
  whitelabel = inject(WhitelabelService);

  private sectionCollapseState = signal<Record<string, boolean>>({});

  classes = computed(() => {
    return sidebarStyles({
      collapsed: this.isCollapsed(),
      variant: this.variant(),
    });
  });

  asSection(content: MenuContent): MenuSection {
    return content as MenuSection;
  }

  asItem(content: MenuContent): MenuItemContent {
    return content as MenuItemContent;
  }

  toggleSection(section: MenuSection) {
    const labelKey = section.labelKey;
    if (!labelKey || !section.isCollapsible) return;

    this.sectionCollapseState.update(state => ({
      ...state,
      [labelKey]: !(state[labelKey] ?? true),
    }));
  }

  isSectionExpanded(section: MenuSection): boolean {
    if (!section.labelKey || !section.isCollapsible) {
      return true;
    }
    return this.sectionCollapseState()[section.labelKey] ?? true;
  }

  getSectionClasses(section: MenuSection) {
    return sidebarSectionStyles({
      sidebarCollapsed: this.isCollapsed(),
      // If no label key is provided, the section is always expanded
      expanded: section.labelKey ? this.isSectionExpanded(section) : true,
    });
  }

  getLinkClassesForItem(item: MenuItemContent) {
    return sidebarLinkStyles({
      active: item.route
        ? this.router.isActive(item.route, {
            paths: 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored',
            ...item.isActiveMatchOptions,
          })
        : false,
      collapsed: this.isCollapsed(),
      variant: this.variant(),
      size: this.size(),
    });
  }

  onLogout() {
    this.logout.emit();
  }
}
